import React, { useState, useEffect } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { ShoppingBag, ArrowDown, Sparkles, ChevronRight, HelpCircle } from "lucide-react";
import ThreeCanvas from "./components/ThreeCanvas";
import Lookbook from "./components/Lookbook";
import ProductCatalog from "./components/ProductCatalog";
import CartDrawer from "./components/CartDrawer";
import StylistChat from "./components/StylistChat";

export default function App() {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Load cart from local storage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem("wovll_cart");
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (err) {
        console.error("Error parsing cart", err);
      }
    }
  }, []);

  // Al volver de Stripe tras pagar: vaciar la bolsa y avisar
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") === "1") {
      localStorage.removeItem("wovll_cart");
      setCartItems([]);
      toast.success("¡Pago completado! Gracias por tu compra en Wovll.", {
        style: { borderRadius: "0px", fontFamily: "'Azeret Mono', monospace", fontSize: "12px" },
      });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Sync cart to local storage
  const saveCart = (items) => {
    setCartItems(items);
    localStorage.setItem("wovll_cart", JSON.stringify(items));
  };

  const handleAddToCart = (newItem) => {
    const existingIndex = cartItems.findIndex(
      (item) =>
        item.productId === newItem.productId &&
        item.size === newItem.size &&
        item.color === newItem.color
    );

    let updatedCart = [...cartItems];
    if (existingIndex > -1) {
      updatedCart[existingIndex].quantity += newItem.quantity;
    } else {
      updatedCart.push(newItem);
    }

    saveCart(updatedCart);
    toast.success(`Añadido a la bolsa: ${newItem.name} (${newItem.size})`, {
      style: {
        borderRadius: "0px",
        fontFamily: "'Azeret Mono', monospace",
        fontSize: "12px"
      }
    });
  };

  const handleUpdateQty = (productId, size, color, newQty) => {
    if (newQty < 1) {
      handleRemoveItem(productId, size, color);
      return;
    }

    const updated = cartItems.map((item) =>
      item.productId === productId && item.size === size && item.color === color
        ? { ...item, quantity: newQty }
        : item
    );
    saveCart(updated);
  };

  const handleRemoveItem = (productId, size, color) => {
    const updated = cartItems.filter(
      (item) =>
        !(item.productId === productId && item.size === size && item.color === color)
    );
    saveCart(updated);
    toast.info("Prenda retirada de la bolsa", {
      style: {
        borderRadius: "0px",
        fontFamily: "'Azeret Mono', monospace",
        fontSize: "12px"
      }
    });
  };

  const handleClearCart = () => {
    saveCart([]);
  };

  const totalCartCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="bg-[#F4F4F2] text-[#0A0A0A] min-h-screen relative font-body selection:bg-[#FF3B30] selection:text-white">
      <Toaster position="top-right" closeButton />

      {/* Crystal Glassmorphism Fixed Navigation */}
      <header className="fixed top-0 w-full z-40 backdrop-blur-2xl bg-white/70 border-b border-[#0A0A0A]/5 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="brand-logo"
            className="font-heading italic text-3xl font-semibold tracking-tighter hover:scale-105 transition-transform"
          >
            Wovll
          </button>

          {/* Nav menu links */}
          <nav className="hidden md:flex items-center gap-12 font-mono-accent text-xs uppercase tracking-widest text-text_secondary">
            <button
              onClick={() => scrollToSection("lookbook")}
              data-testid="nav-lookbook"
              className="hover:text-[#0A0A0A] hover:underline underline-offset-8 transition-colors"
            >
              Lookbook 2026
            </button>
            <button
              onClick={() => scrollToSection("shop")}
              data-testid="nav-shop"
              className="hover:text-[#0A0A0A] hover:underline underline-offset-8 transition-colors"
            >
              Tienda / Colección
            </button>
          </nav>

          {/* Cart triggers */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsCartOpen(true)}
              data-testid="cart-toggle"
              className="relative p-2 hover:scale-110 transition-transform text-[#0A0A0A]"
              aria-label="Ver Bolsa de Compra"
            >
              <ShoppingBag className="w-5 h-5" />
              {totalCartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#FF3B30] text-white text-[9px] font-mono-accent rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {totalCartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Hero section */}
      <section className="h-screen w-full relative overflow-hidden flex flex-col justify-between pt-20">
        {/* Background 3D Canvas Canvas with mouse tilt */}
        <div className="absolute inset-0 z-0">
          <ThreeCanvas shapeType="torusKnot" color="#4F46E5" />
        </div>

        {/* Hero typography elements overlay */}
        <div className="relative z-10 flex-1 flex flex-col justify-center items-center text-center px-6">
          <span className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#4F46E5] mb-4 animate-pulse">
            HIGH-CONTRAST SWISS STREETWEAR
          </span>
          <h1 className="font-heading italic text-7xl md:text-9xl font-semibold tracking-tighter leading-none mb-6">
            W O V L L
          </h1>
          <p className="font-body text-base md:text-xl font-light text-text_secondary max-w-xl leading-relaxed mb-12">
            La fusión perfecta entre la precisión minimalista suiza y la energía cruda del streetwear urbano en tres dimensiones.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => scrollToSection("shop")}
              data-testid="hero-shop-button"
              className="px-8 py-4 bg-[#0A0A0A] text-white hover:bg-[#FF3B30] transition-colors font-mono-accent text-xs uppercase tracking-widest rounded-none"
            >
              Comprar Colección
            </button>
            <button
              onClick={() => scrollToSection("lookbook")}
              data-testid="hero-lookbook-button"
              className="px-8 py-4 bg-white/70 backdrop-blur border border-border_subtle text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors font-mono-accent text-xs uppercase tracking-widest rounded-none"
            >
              Explorar Lookbook
            </button>
          </div>
        </div>

        {/* Decender CTA */}
        <div className="relative z-10 pb-8 flex flex-col items-center cursor-pointer" onClick={() => scrollToSection("lookbook")}>
          <span className="font-mono-accent text-[9px] uppercase tracking-[0.2em] text-text_secondary mb-2">
            DESCENDER // SCROLL DOWN
          </span>
          <ArrowDown className="w-4 h-4 text-[#0A0A0A] animate-bounce" />
        </div>
      </section>

      {/* Lookbook section */}
      <Lookbook />

      {/* Shop section */}
      <ProductCatalog onAddToCart={handleAddToCart} />

      {/* Simulated Cart Drawer */}
      <CartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        onUpdateQty={handleUpdateQty}
        onRemoveItem={handleRemoveItem}
        onClearCart={handleClearCart}
      />

      {/* AI Stylist persistent chat assistant */}
      <StylistChat />

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white py-16 font-body border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          {/* Brand info */}
          <div className="md:col-span-4 space-y-4">
            <h3 className="font-heading italic text-4xl font-semibold leading-none">Wovll</h3>
            <p className="text-xs text-white/50 leading-relaxed font-light max-w-xs">
              Precisión geométrica y costuras de alta densidad. Diseñado en Suiza, producido de forma ética y sostenible para la cultura global.
            </p>
          </div>

          {/* Links 1 */}
          <div className="md:col-span-4 space-y-4">
            <h4 className="font-mono-accent text-xs uppercase tracking-widest text-white/40">Zonas de Diseño</h4>
            <ul className="space-y-2 text-xs font-light text-white/70">
              <li>Estudio 01 — Zurich, Suiza</li>
              <li>Taller de Corte — Tokio, Japón</li>
              <li>Showroom — Berlín, Alemania</li>
            </ul>
          </div>

          {/* Links 2 */}
          <div className="md:col-span-4 space-y-4">
            <h4 className="font-mono-accent text-xs uppercase tracking-widest text-white/40">Garantía Wovll</h4>
            <p className="text-xs text-white/60 font-light leading-relaxed">
              Cada prenda Wovll incluye una etiqueta de autenticidad NFC y un empaque reutilizable hecho de fibras de piña 100% compostables.
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
              {[
                ["aviso", "Aviso legal"],
                ["privacidad", "Privacidad"],
                ["terminos", "Términos"],
                ["envios", "Envíos"],
                ["devoluciones", "Devoluciones"],
              ].map(([id, label]) => (
                <a
                  key={id}
                  href={`/legal#${id}`}
                  className="font-mono-accent text-[10px] uppercase tracking-widest text-white/60 hover:text-white transition-colors"
                >
                  {label}
                </a>
              ))}
            </div>
            <div className="font-mono-accent text-[10px] text-[#FF3B30] uppercase tracking-widest">
              © 2026 WOVLL INC. ALL RIGHTS RESERVED
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
